import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = 'https://aaktradex.in';

const C = {
  green: '#3B6D11', lgreen: '#EAF3DE', bgreen: '#C0DD97',
  amber: '#854F0B', lamber: '#FAEEDA', bamber: '#FAC775',
  red:   '#A32D2D', lred:   '#FCEBEB', bred:   '#F7C1C1',
  blue:  '#185FA5', lblue:  '#E6F1FB', bblue:  '#B5D4F4',
  teal:  '#0D7377', lteal:  '#E0F2F1', bteal:  '#80CBC4',
  gray:  '#6B7280', lgray:  '#F3F4F6', bgray:  '#D1D5DB',
};

function fmt(n, dp=2) {
  if (n == null) return '—';
  return typeof n === 'number' ? n.toFixed(dp) : n;
}

function timeAgo(iso) {
  if (!iso) return '—';
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const secs = Math.floor((now - then) / 1000);
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    return `${Math.floor(secs / 3600)}h ago`;
  } catch { return '—'; }
}

function StatChip({ label, value, color }) {
  return (
    <div style={{ padding: 8, background: 'var(--bg2)', borderRadius: 6, minWidth: 100 }}>
      <div style={{ fontSize: 9, color: 'var(--t2)', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: color || 'var(--t1)' }}>{value}</div>
    </div>
  );
}

function ConditionPill({ met, label }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 6px', borderRadius: 3,
      fontSize: 9, fontWeight: 600, margin: '0 2px',
      background: met ? C.lgreen : C.lred,
      color: met ? C.green : C.red,
    }} title={label}>
      {label.slice(0, 4)}
    </span>
  );
}

export default function TickMonitor() {
  const [health, setHealth] = useState(null);
  const [states, setStates] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [h, s] = await Promise.all([
        axios.get(`${API}/tick/health`).catch(() => ({ data: null })),
        axios.get(`${API}/tick/state`).catch(() => ({ data: null })),
      ]);
      setHealth(h.data);
      setStates(s.data?.states || {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 30000);
    return () => clearInterval(t);
  }, [fetchAll]);

  if (loading) return <div style={{ padding: 16, fontSize: 11 }}>Loading Tick Monitor...</div>;

  const tickerKeys = Object.keys(states).sort();

  return (
    <div style={{ padding: 16 }}>
      {/* Header banner */}
      <div style={{
        background: C.lblue, color: C.blue,
        padding: 12, borderRadius: 10, marginBottom: 12,
        border: `1px solid ${C.bblue}`,
      }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>Tick Pipeline Monitor</div>
        <div style={{ fontSize: 10, opacity: 0.85, marginTop: 3 }}>
          Live tick stream from Kite · Redis-backed intraday state · Path A entry decisions
        </div>
      </div>

      {/* Health stats */}
      {health && (
        <div style={{
          display: 'flex', gap: 8, flexWrap: 'wrap',
          marginBottom: 12, padding: 12, background: 'var(--bg1)',
          borderRadius: 10, border: '1px solid var(--border2)',
        }}>
          <StatChip label="SUBSCRIBED" value={health.subscribed_count} color={C.teal} />
          <StatChip label="FRESH" value={health.fresh_count} color={health.fresh_count > 0 ? C.green : C.gray} />
          <StatChip label="STALE" value={(health.stale_tickers || []).length} color={C.red} />
          <StatChip label="TICKS TODAY" value={health.total_ticks_today} />
          <StatChip label="LAST PING" value={timeAgo(health.last_ping)} />
        </div>
      )}

      {/* Per-ticker state */}
      <div style={{
        background: 'var(--bg1)', borderRadius: 10,
        border: '1px solid var(--border2)', padding: 14,
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
          Per-Ticker State ({tickerKeys.length})
        </div>
        <div style={{ overflow: 'auto' }}>
          <table style={{ fontSize: 10, width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.bgray}` }}>
                <th style={{ textAlign: 'left', padding: 5 }}>Ticker</th>
                <th style={{ textAlign: 'right', padding: 5 }}>LTP</th>
                <th style={{ textAlign: 'left', padding: 5 }}>Age</th>
                <th style={{ textAlign: 'right', padding: 5 }}>Bars</th>
                <th style={{ textAlign: 'right', padding: 5 }}>RSI</th>
                <th style={{ textAlign: 'right', padding: 5 }}>RSI min</th>
                <th style={{ textAlign: 'right', padding: 5 }}>MACD H</th>
                <th style={{ textAlign: 'right', padding: 5 }}>VWAP Δ%</th>
                <th style={{ textAlign: 'left', padding: 5 }}>Path A</th>
              </tr>
            </thead>
            <tbody>
              {tickerKeys.map(ticker => {
                const stateObj = states[ticker];
                const state = stateObj?.state;
                const tick = stateObj?.latest_tick || state?.latest_ltp
                  ? { ltp: (stateObj?.latest_tick?.ltp || state?.latest_ltp), ts: stateObj?.latest_tick?.ts }
                  : null;

                if (!state) {
                  return (
                    <tr key={ticker} style={{ borderBottom: `0.5px solid ${C.lgray}` }}>
                      <td style={{ padding: 5, fontWeight: 600 }}>{ticker}</td>
                      <td style={{ padding: 5, textAlign: 'right' }}>{tick?.ltp ? fmt(tick.ltp) : '—'}</td>
                      <td style={{ padding: 5, fontSize: 9, color: 'var(--t3)' }}>{timeAgo(tick?.ts)}</td>
                      <td colSpan={6} style={{ padding: 5, fontSize: 9, color: 'var(--t3)', fontStyle: 'italic' }}>
                        Warming up (need &gt;=15 bars)
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr key={ticker} style={{ borderBottom: `0.5px solid ${C.lgray}` }}>
                    <td style={{ padding: 5, fontWeight: 600 }}>{ticker}</td>
                    <td style={{ padding: 5, textAlign: 'right' }}>{fmt(state.latest_ltp)}</td>
                    <td style={{ padding: 5, fontSize: 9, color: state.stream_healthy ? C.green : C.red }}>
                      {timeAgo(tick?.ts)}
                    </td>
                    <td style={{ padding: 5, textAlign: 'right' }}>{state.bars_count}</td>
                    <td style={{ padding: 5, textAlign: 'right',
                      color: state.rsi_now < 45 ? C.red : state.rsi_now > 70 ? C.amber : C.green }}>
                      {fmt(state.rsi_now, 1)}
                    </td>
                    <td style={{ padding: 5, textAlign: 'right', color: 'var(--t3)' }}>
                      {fmt(state.rsi_min_recent, 1)}
                    </td>
                    <td style={{ padding: 5, textAlign: 'right',
                      color: (state.macd_hist_now || 0) > 0 ? C.green : C.red }}>
                      {fmt(state.macd_hist_now, 3)}
                    </td>
                    <td style={{ padding: 5, textAlign: 'right',
                      color: (state.price_vs_vwap_pct || 0) > 0 ? C.green : C.red }}>
                      {fmt(state.price_vs_vwap_pct, 2)}
                    </td>
                    <td style={{ padding: 5 }}>
                      <ConditionPill met={state.volatility_settled} label="Volatility" />
                      <ConditionPill met={state.was_oversold_recently} label="Oversold" />
                      <ConditionPill met={state.recovering} label="Recovering" />
                      <ConditionPill met={state.macd_hist_flipped_positive} label="MACDflip" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
